import { context, trace } from '@opentelemetry/api';
import { logger } from '@parrot-software/observability';

import type { ISubscriptionBlockApiPort } from '@subscriptions/application/ports';
import type { UpdateBlockPaymentMethodDto, SubscriptionBlockViewDto } from '@subscriptions/application/dtos';
import { mapSubscriptionBlockToViewDto } from '@subscriptions/application/mappers';
import { extractErrorAttributes } from '@subscriptions/application/utils';

const subLog = logger.forDomain('SUB', { subdomain: 'lifecycle' });
const tracer = trace.getTracer('@parrot-software/observability');

/**
 * Use case for updating the payment method of a subscription block.
 *
 * @param port - Subscription block API port
 * @returns Use case executor
 */
export const updateBlockPaymentMethodUseCase = (port: ISubscriptionBlockApiPort) => {
  const execute = async (blockUuid: string, dto: UpdateBlockPaymentMethodDto): Promise<SubscriptionBlockViewDto> => {
    // `dto.providerId` is intentionally NOT logged: for SPEI/Fintoc it can be a
    // CLABE (18 digits) which is PII per Charter §5.1. Correlation with the
    // payment provider is preserved via `view.paymentMethod.uuid` (internal id).
    const span = tracer.startSpan('SUB.updateBlockPaymentMethod', {
      attributes: {
        'block.uuid': blockUuid,
        'payment.provider': dto.provider,
      },
    });
    const ctx = trace.setSpan(context.active(), span);

    return context.with(ctx, async () => {
      try {
        const block = await port.updatePaymentMethod(blockUuid, dto);
        const view = mapSubscriptionBlockToViewDto(block);
        subLog.info('updateBlockPaymentMethod', {
          'block.uuid': blockUuid,
          'payment.uuid': view.paymentMethod?.uuid,
          'payment.provider': dto.provider,
          'payment.methodType': view.paymentMethod?.methodType,
        });
        return view;
      } catch (error) {
        subLog.error('updateBlockPaymentMethod', {
          ...extractErrorAttributes(error),
          'block.uuid': blockUuid,
        });
        throw error;
      } finally {
        span.end();
      }
    });
  };

  return { execute };
};
